<template>
  <main class="notifications-page">
    <header class="notifications-page__header">
      <div>
        <p>Inbox</p>
        <h1>Notifications</h1>
        <span>Mentions, starred-shipment activity, and operational assignments.</span>
      </div>
      <div class="notifications-page__actions">
        <Button variant="soft" tone="neutral" size="sm" :disabled="busy || archived" @click="markAllRead">Mark all read</Button>
        <Button variant="soft" tone="neutral" size="sm" :disabled="busy || archived" @click="clearRead">Clear read</Button>
        <Button variant="soft" tone="danger" size="sm" :disabled="busy || archived" @click="clearAll">Clear all</Button>
      </div>
    </header>

    <div class="notifications-page__tabs" role="tablist">
      <button type="button" role="tab" :aria-selected="!archived" :class="{ active: !archived }" @click="archived = false">Active</button>
      <button type="button" role="tab" :aria-selected="archived" :class="{ active: archived }" @click="archived = true">Archived</button>
    </div>

    <p v-if="errorMessage" class="notifications-page__status notifications-page__status--error">{{ errorMessage }}</p>
    <p v-else-if="loading" class="notifications-page__status">Loading notifications…</p>
    <p v-else-if="items.length === 0" class="notifications-page__status">No {{ archived ? 'archived' : 'active' }} notifications.</p>
    <ul v-else class="notifications-page__list">
      <li v-for="notification in items" :key="notification.id">
        <button type="button" :class="{ unread: !notification.readAt }" @click="openNotification(notification)">
          <i class="material-icons" aria-hidden="true">{{ notification.type === 'mention' ? 'alternate_email' : 'notifications' }}</i>
          <span>
            <strong>{{ notification.title }}</strong>
            <small>{{ notification.preview }}</small>
            <time>{{ formatTime(notification.createdAt) }} · {{ notification.subject.label }}</time>
          </span>
        </button>
      </li>
    </ul>
  </main>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue';
import type { Timestamp, Unsubscribe } from 'firebase/firestore';
import { Button } from '@/components/ui';
import { getCurrentUser } from '@/modules/firebase/Auth';
import {
  archiveNotifications,
  markNotificationRead,
  markNotificationsRead,
  subscribeNotifications,
  type UserNotification
} from '@/modules/notifications/NotificationClient';

const user = getCurrentUser();
const archived = ref(false);
const items = ref<UserNotification[]>([]);
const loading = ref(true);
const busy = ref(false);
const errorMessage = ref('');
let unsubscribe: Unsubscribe | null = null;

function load(): void {
  unsubscribe?.();
  items.value = [];
  loading.value = true;
  if (!user?.uid) return;
  unsubscribe = subscribeNotifications(user.uid, archived.value, notifications => {
    items.value = notifications;
    loading.value = false;
    errorMessage.value = '';
  }, error => {
    errorMessage.value = error.message;
    loading.value = false;
  });
}

async function mutate(action: () => Promise<unknown>): Promise<void> {
  busy.value = true;
  errorMessage.value = '';
  try { await action(); } catch (error) { errorMessage.value = error instanceof Error ? error.message : 'Notification action failed.'; }
  finally { busy.value = false; }
}

function markAllRead(): void { void mutate(() => markNotificationsRead({ scope: 'all' })); }
function clearRead(): void { void mutate(() => archiveNotifications({ scope: 'read' })); }
function clearAll(): void { void mutate(() => archiveNotifications({ scope: 'all' })); }

async function openNotification(notification: UserNotification): Promise<void> {
  if (!notification.readAt) await markNotificationRead(notification.id).catch(error => { errorMessage.value = error.message; });
  window.location.assign(notification.actionPath);
}

function formatTime(timestamp: Timestamp): string {
  return timestamp?.toDate ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(timestamp.toDate()) : '';
}

watch(archived, load, { immediate: true });
onBeforeUnmount(() => unsubscribe?.());
</script>
